import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import type {
  TableRowActionsMenuItemProps,
  TableRowActionsMenuProps,
} from '@/components/common/types/table-row-actions-menu.types'

const CloseMenuContext = createContext<(() => void) | null>(null)

function useCloseTableRowActionsMenu(): () => void {
  const close = useContext(CloseMenuContext)
  return close ?? (() => {})
}

const menuItemBaseClass =
  'flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed dark:hover:bg-gray-700'

const menuItemVariantClass: Record<
  NonNullable<TableRowActionsMenuItemProps['variant']>,
  string
> = {
  default: 'text-gray-700 dark:text-gray-100',
  destructive:
    'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30',
}

const MENU_GAP = 4
const VIEWPORT_PADDING = 8

export function TableRowActionsMenuItem({
  icon,
  children,
  onSelect,
  variant = 'default',
  disabled = false,
}: TableRowActionsMenuItemProps): React.ReactElement {
  const closeMenu = useCloseTableRowActionsMenu()

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={cn(menuItemBaseClass, menuItemVariantClass[variant], disabled && 'opacity-50')}
      onClick={() => {
        closeMenu()
        onSelect()
      }}
    >
      {icon}
      {children}
    </button>
  )
}

TableRowActionsMenuItem.displayName = 'TableRowActionsMenuItem'

export function TableRowActionsMenuDivider(): React.ReactElement {
  return (
    <div
      role="separator"
      className="my-1 border-t border-gray-200 dark:border-gray-600"
      aria-hidden
    />
  )
}

TableRowActionsMenuDivider.displayName = 'TableRowActionsMenuDivider'

export function TableRowActionsMenu({
  rowId,
  openRowId,
  onOpenRowIdChange,
  triggerAriaLabel,
  menuAriaLabel,
  children,
}: TableRowActionsMenuProps): React.ReactElement {
  const isOpen = openRowId === rowId
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    right: number
    maxHeight: number
  } | null>(null)

  const closeMenu = useCallback((): void => {
    onOpenRowIdChange(null)
  }, [onOpenRowIdChange])

  const updatePosition = useCallback((): void => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight ?? 0
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PADDING
    const spaceAbove = rect.top - MENU_GAP - VIEWPORT_PADDING
    const openUp = menuHeight > 0 && spaceBelow < menuHeight && spaceAbove > spaceBelow
    const availableHeight = Math.max(
      120,
      openUp ? spaceAbove : spaceBelow,
    )
    const top = openUp
      ? Math.max(VIEWPORT_PADDING, rect.top - MENU_GAP - menuHeight)
      : Math.min(
        rect.bottom + MENU_GAP,
        window.innerHeight - VIEWPORT_PADDING - Math.min(menuHeight, availableHeight),
      )
    const right = Math.max(VIEWPORT_PADDING, window.innerWidth - rect.right)

    setMenuPosition((current) => {
      const next = { top, right, maxHeight: availableHeight }
      if (
        current?.top === next.top &&
        current.right === next.right &&
        current.maxHeight === next.maxHeight
      ) {
        return current
      }
      return next
    })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, menuPosition, updatePosition])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeMenu()
    }
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      closeMenu()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, closeMenu])

  return (
    <>
      <div ref={triggerRef} className="relative flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onOpenRowIdChange(isOpen ? null : rowId)}
          className="h-8 w-8 shrink-0 cursor-pointer p-0 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={triggerAriaLabel}
        >
          <MoreVertical className="h-4 w-4" aria-hidden />
          <span className="sr-only">Open actions</span>
        </Button>
      </div>
      {isOpen &&
        menuPosition &&
        createPortal(
          <CloseMenuContext.Provider value={closeMenu}>
            <div
              ref={menuRef}
              role="menu"
              aria-label={menuAriaLabel}
              className="min-w-44 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                right: menuPosition.right,
                maxHeight: menuPosition.maxHeight,
                zIndex: 9999,
              }}
            >
              {children}
            </div>
          </CloseMenuContext.Provider>,
          document.body
        )}
    </>
  )
}

TableRowActionsMenu.displayName = 'TableRowActionsMenu'
